// ABOUTME: Main dashboard component implementing the Material-UI redesign specification
// ABOUTME: Provides behavior tracking interface with child rows and floating microphone

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { alpha } from '@mui/material/styles';
import { celebrationGold, celebrationGoldHover, starAccent, decorativeGradients } from '../theme/tokens';
import naluV1Theme from '../theme/theme.nalu-v1';
import {
  Box,
  Container,
  Fab,
  IconButton,
  Paper,
  Typography,
  Button,
  Snackbar,
  Alert,
  Avatar,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  useMediaQuery,
  useTheme,
  TablePagination,
  Tabs,
  Tab,
} from '@mui/material';
import {
  StarOutlineRounded,
  Mic as MicIcon,
  NavigateNext as NavigateNextIcon,
  Celebration as CelebrationIcon,
  EmojiEvents as TrophyIcon,
  FavoriteRounded as HeartIcon,
  Star as StarIcon,
  Repeat as RepeatIcon,
  AutoDelete as NukeIcon,
  FlightTakeoff as FlightTakeoffIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSignalR } from '../contexts/SignalRContext';
import { useFamilyTerminology } from '../hooks/useFamilyTerminology';
import { useSiteSettings } from '../hooks/useSiteSettings';
import SharedAppBar from './SharedAppBar';
import { Family, Child, EnhancedFamilyDashboard, BehaviorType, Goal, GoalType, GoalStatus, ChoreListPeriodStatus, ChoreListType, ChoreListStatus, CompletedGoalInfo, GoalProgressIndicator as GoalProgressData, DailyProgress } from '../types/api';
import { deriveHeartDates } from './HeartCalendarUtils';
import { mergeChoreStatusesByType } from '../utils/choreListUtils';
import { familyAPI, behaviorAPI, goalsAPI, choresAPI, awayScheduleAPI } from '../services/api';
import FamilySetupWizard from './FamilySetupWizard';
import BehaviorCountBadge from './BehaviorCountBadge';
import BehaviorTallyButton from './BehaviorTallyButton';
import FlipYourCoinButton from './FlipYourCoinButton';
import OneOnOneTimeButton from './OneOnOneTimeButton';
import MakeItRightButton from './MakeItRightButton';
import CelebrationModal from './CelebrationModal';
import { ChildContentRow } from './ChildContentRow';
import MakeItRightApprovalRibbon from './MakeItRightApprovalRibbon';
import ResetAndReflectApprovalRibbon from './ResetAndReflectApprovalRibbon';
import HeartCalendar from './HeartCalendar';
import AnalyticsTimeChart from './AnalyticsTimeChart';
import AllKidsQuickRecordTab from './AllKidsQuickRecordTab';
import CrossingListForChild from './Crossing/CrossingListForChild';
import ParentResetButton from './Crossing/ParentResetButton';
import CrossingListAllKids from './Crossing/CrossingListAllKids';
import { FamilyStrip } from './FamilyStrip/FamilyStrip';
import { getTodayInFamilyTimezone, getDateInFamilyTimezone } from '../utils/dateUtils';

import ResponsibilitiesApprovalRibbon from './ResponsibilitiesApprovalRibbon';
import { getFullApiUrl } from '../utils/routeUtils';
import { useCelebration } from '../hooks/useCelebration';
import { getChildColor, getChildBorderColor } from '../utils/childColorUtils';
import { getBehaviorLabel, StormIcon, TriangleIcon } from '../utils/behaviorUtils';

interface BehaviorUIType {
  type: 'star' | 'triangle' | 'square';
  icon: React.ElementType;
  label: string;
  color: string;
}

const behaviorTypes: BehaviorUIType[] = [
  { type: 'square', icon: StormIcon, label: 'Consequence', color: 'error.main' },
  { type: 'triangle', icon: TriangleIcon, label: 'Warning', color: 'warning.main' },
  { type: 'star', icon: StarOutlineRounded, label: 'Positive Behavior', color: 'success.main' },
];

// Dashboard tab panel + a11y helpers (issue #1767) — mirrors the pattern in ChildProfilePage.
// All panels are eager-mounted; `hidden` controls visibility so tab switches are instant.
interface DashboardTabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function DashboardTabPanel({ children, value, index }: DashboardTabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
    >
      <Box>{children}</Box>
    </div>
  );
}

function dashboardA11yProps(index: number) {
  return {
    id: `dashboard-tab-${index}`,
    'aria-controls': `dashboard-tabpanel-${index}`,
  };
}

// Issue #1415: Helper to merge celebrating goals with active goals for rendering
interface GoalWithCelebration extends GoalProgressData {
  key: string;
  isCelebrating: boolean;
}

const mergeGoalsForChild = (
  childId: number,
  activeGoals: GoalProgressData[],
  celebratingGoals: Map<string, CompletedGoalInfo>
): GoalWithCelebration[] => {
  // Get celebrating goals for this child
  const childCelebratingGoals = Array.from(celebratingGoals.entries())
    .filter(([key]) => key.startsWith(`${childId}-`))
    .map(([key, goal]) => ({ ...goal, key }));

  // Convert CompletedGoalInfo to GoalProgressData format for celebrating goals
  const celebratingAsProgress: GoalWithCelebration[] = childCelebratingGoals.map((goal) => ({
    goalId: goal.goalId,
    goalTitle: goal.goalTitle,
    rewardText: goal.rewardText,
    goalType: goal.goalType,
    currentValue: goal.currentValue,
    targetValue: goal.targetValue,
    dayPasses: goal.dayPasses,
    dayPassesUsed: goal.dayPassesUsed,
    daysRemaining: goal.daysRemaining,
    endDate: goal.endDate ?? undefined,
    progressIcons: [],
    progressText: `${goal.currentValue}/${goal.targetValue}`,
    isRecurring: goal.isRecurring,
    startDate: goal.startDate,
    key: goal.key,
    isCelebrating: true,
  }));

  // Merge with active goals (mark any that are also celebrating)
  const celebratingGoalIds = new Set(childCelebratingGoals.map((g) => g.goalId));
  const activeGoalsWithCelebration: GoalWithCelebration[] = activeGoals.map((goal) => ({
    ...goal,
    key: `active-${goal.goalId}`,
    isCelebrating: celebratingGoalIds.has(goal.goalId),
  }));

  // Add celebrating goals that aren't in active goals (just completed)
  const additionalCelebratingGoals = celebratingAsProgress.filter(
    (g) => !activeGoals.some((ag) => ag.goalId === g.goalId)
  );

  const allGoals = [...activeGoalsWithCelebration, ...additionalCelebratingGoals];

  // Sort: celebrating goals at top (100%), then by progress descending
  return allGoals.sort((a, b) => {
    if (a.isCelebrating && !b.isCelebrating) return -1;
    if (!a.isCelebrating && b.isCelebrating) return 1;
    const aProgress = (a.currentValue / a.targetValue) * 100;
    const bProgress = (b.currentValue / b.targetValue) * 100;
    return bProgress - aProgress;
  });
};

const DashboardRedesigned: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { basePath } = useFamilyTerminology();
  const { on, connectionState } = useSignalR();
  const { data: siteSettings } = useSiteSettings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // nalu-v1's dashboard tabs render as pills (issue #1900) with no divider beneath them.
  // Compared by value (logoSrc), not object identity — dev-server HMR can give the
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // object references after a hot-reload even though both are "the nalu-v1 theme".
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;

  const [, setFamilies] = useState<Family[]>([]);
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [enhancedDashboard, setEnhancedDashboard] = useState<EnhancedFamilyDashboard | null>(null);
  const [goalsReadyToCelebrate, setGoalsReadyToCelebrate] = useState<Goal[]>([]);
  const [totalCelebrateCount, setTotalCelebrateCount] = useState(0);
  const [celebratePage, setCelebratePage] = useState(0);
  const [celebrateRowsPerPage, setCelebrateRowsPerPage] = useState(5);
  const hasLoadedCelebrateOnce = useRef(false);
  // Track previous connectionState to detect Disconnected→Connected transitions for re-fetch
  const prevConnectionStateRef = useRef<string>(connectionState);
  // Refs so SignalR handlers always see current values without stale closures
  const selectedFamilyRef = useRef(selectedFamily);
  selectedFamilyRef.current = selectedFamily;
  // Tracks the familyId from the last intentional family switch (issue #1776).
  // Lets the selectedFamily effect compare by identity rather than object reference,
  // preventing spurious resets when a stale SignalR closure re-calls setSelectedFamily
  // with a new object for the same family.
  const prevFamilyIdRef = useRef<number | null>(null);
  const enhancedDashboardRef = useRef(enhancedDashboard);
  enhancedDashboardRef.current = enhancedDashboard;
  // Historical chore statuses per child when viewing a past date on the heart calendar
  const [historicalChildChoreStatuses, setHistoricalChildChoreStatuses] = useState<Record<number, ChoreListPeriodStatus[]>>({});
  // Chore statuses are bundled into enhancedDashboard.children per child (issue #1742)
  // Derived as a Record for compatibility with ribbon components and toggle handler
  const childChoreStatuses = useMemo<Record<number, ChoreListPeriodStatus[]>>(
    () => enhancedDashboard
      ? Object.fromEntries(enhancedDashboard.children.map(c => [c.childId, c.choreStatuses]))
      : {},
    [enhancedDashboard]
  );
  const [celebrationModal, setCelebrationModal] = useState<{
    isOpen: boolean;
    goal: Goal | null;
    childName: string;
  }>({ isOpen: false, goal: null, childName: '' });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  // Dashboard tab selection (issue #1767): 0 = "All Kids" quick-record; 1..N = child index + 1
  const [activeTab, setActiveTab] = useState(0);
  // The child whose full tab is active, or null on the "All Kids" tab. Celebrations are
  // scoped to this child so there is no family-wide celebration aggregate (issue #1767).
  const activeChildId = activeTab > 0
    ? (enhancedDashboard?.children[activeTab - 1]?.childId ?? null)
    : null;
  // Restores the tab the parent was on before opening Crossing (Issue #1940 feedback: Save &
  // Exit always landed on "All Kids", even when opened from a specific child's tab). Tabs are
  // pure component state, not part of the URL, so CrossingRunnerFrame hands the child id back via
  // navigate's state; applied once enhancedDashboard has loaded, and only once per navigation.
  const restoredReturnChildIdRef = useRef<number | null>(null);
  useEffect(() => {
    const returnChildId = (location.state as { activeChildId?: number } | null)?.activeChildId;
    if (returnChildId == null || !enhancedDashboard || restoredReturnChildIdRef.current === returnChildId) return;
    const index = enhancedDashboard.children.findIndex(c => c.childId === returnChildId);
    if (index >= 0) {
      restoredReturnChildIdRef.current = returnChildId;
      setActiveTab(index + 1);
    }
  }, [location.state, enhancedDashboard]);
  const [showFamilySetup, setShowFamilySetup] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [error, setError] = useState<string | null>(null);
  const [highlightedListId, setHighlightedListId] = useState<number | null>(null);
  const highlightClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Auto-collapse state for completed chore lists (Issue #1515)
  const [autoCollapseListIds, setAutoCollapseListIds] = useState<Set<number>>(new Set());
  const { celebrate, badCelebration } = useCelebration();
  const [nukeState, setNukeState] = useState<{ childId: number | null; loading: boolean; error: string | null }>({
    childId: null,
    loading: false,
    error: null,
  });

  // Issue #1520: Per-child calendar and timeline state
  interface ChildCalendarState {
    heartDates: string[];
    mendingHeartDates: string[];
    heartLoading: boolean;
    selectedDate: string;           // 'YYYY-MM-DD' for AnalyticsTimeChart
    selectedCalendarDate: Date;     // Date object for HeartCalendar
    dailyProgress: DailyProgress[]; // Full progress records for tooltip display
  }
  const [childCalendarState, setChildCalendarState] = useState<Record<number, ChildCalendarState>>({});
  const childCalendarStateRef = useRef(childCalendarState);
  childCalendarStateRef.current = childCalendarState;
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);

  // Issue #1415: State for newly completed goals that should be celebrating
  // Map key is `${childId}-${goalId}` for uniqueness
  const [celebratingGoals, setCelebratingGoals] = useState<Map<string, CompletedGoalInfo>>(new Map());

  // Away day state (issue #1706)
  const [isTodayAwayDay, setIsTodayAwayDay] = useState(false);
  // Pending behavior click held while the away-day confirmation dialog is open
  const [awayDayConfirmPending, setAwayDayConfirmPending] = useState<null | { childId: number | string; behaviorType: 'star' | 'triangle' | 'square' | BehaviorType }>(null);
  // When true, skip the away-day confirmation dialog for the rest of today
  const [awayDayDismissedForToday, setAwayDayDismissedForToday] = useState(false);

  // Track timers for cleanup on unmount
  const celebrationTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Issue #1415: Clean up celebration timers on unmount
  useEffect(() => {
    const timers = celebrationTimersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  // Issue #1482: Show error message when Alexa linking failed during social OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('alexa_linking_error') === 'true') {
      setSnackbar({
        open: true,
        message: 'Alexa account linking failed. Please return to the Alexa app and try again.',
        severity: 'error',
      });
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Issue #1415: Handle when a goal celebration fade-out completes
  const handleGoalFadeComplete = useCallback((celebrationKey: string) => {
    setCelebratingGoals((prev) => {
      const next = new Map(prev);
      next.delete(celebrationKey);
      return next;
    });
    // Clean up the timer ref
    const timer = celebrationTimersRef.current.get(celebrationKey);
    if (timer) {
      clearTimeout(timer);
      celebrationTimersRef.current.delete(celebrationKey);
    }
  }, []);

  useEffect(() => {
    // Only load dashboard data after user is authenticated
    if (user) {
      loadEnhancedDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadEnhancedDashboard intentionally omitted; it is stable and adding it would cause extra fetches
  }, [user]);

  // Re-fetch dashboard when SignalR reconnects (Disconnected→Connected transition).
  // Uses a ref for previous state so initial Connected render doesn't trigger a redundant fetch.
  useEffect(() => {
    if (prevConnectionStateRef.current !== 'Connected' && connectionState === 'Connected') {
      loadEnhancedDashboard();
    }
    prevConnectionStateRef.current = connectionState;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadEnhancedDashboard intentionally omitted; it is stable and adding it would cause extra fetches
  }, [connectionState]);

  useEffect(() => {
    if (selectedFamily) {
      loadChildren(selectedFamily.familyId);
      // Clear stale celebration data from previous family
      setGoalsReadyToCelebrate([]);
      setTotalCelebrateCount(0);
      setCelebratePage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadChildren intentionally omitted; it is stable and adding it would cause extra fetches
  }, [selectedFamily]);

  // Load away status for today when family changes (issue #1706)
  useEffect(() => {
    if (!selectedFamily) return;
    const today = getTodayInFamilyTimezone(selectedFamily.timezone);
    awayScheduleAPI.getAwayDatesInRange(selectedFamily.familyId, today, today)
      .then(dates => setIsTodayAwayDay(dates.length > 0))
      .catch(() => setIsTodayAwayDay(false));
  }, [selectedFamily]);

  // Issue #1767: land on the "All Kids" tab when the family actually changes.
  // Issue #1776: compare by familyId instead of object reference — stale SignalR
  // handlers can call setSelectedFamily with a fresh object for the same family,
  // which would spuriously reset the active child tab.
  useEffect(() => {
    const id = selectedFamily?.familyId ?? null;
    if (id !== prevFamilyIdRef.current) {
      prevFamilyIdRef.current = id;
      setActiveTab(0);
    }
  }, [selectedFamily]);

  // Issue #1767: if the active child's tab disappears (e.g. child removed via SignalR),
  // fall back to the "All Kids" tab so we never point at a non-existent panel.
  // Issue #1776: skip the guard when enhancedDashboard is null (load in progress) —
  // treating null as 0 children would reset the tab on every mid-reload snapshot.
  useEffect(() => {
    if (!enhancedDashboard) return;
    const count = enhancedDashboard.children.length;
    if (activeTab > count) {
      setActiveTab(0);
    }
  }, [enhancedDashboard, activeTab]);

  // Reset celebration pagination when the active child changes so each child's list starts on page 1
  useEffect(() => {
    setCelebratePage(0);
  }, [activeChildId]);

  // Consolidated celebration goal loading — covers initial load, family switch, active child, and pagination
  useEffect(() => {
    if (selectedFamily) {
      loadGoalsReadyToCelebrate();
    }
  }, [celebratePage, celebrateRowsPerPage, selectedFamily, activeChildId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize calendar state when enhanced dashboard is available
  useEffect(() => {
    if (enhancedDashboard && selectedFamily) {
      // Issue #1520: Load heart dates for current month for children not yet initialized.
      // Skip children that already have calendar state — hearts only change at reconciliation
      // (3am), so reloading on every behavior event is unnecessary and causes calendar flicker.
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      enhancedDashboard.children.forEach(enhChild => {
        if (!childCalendarStateRef.current[enhChild.childId]) {
          const fullChild = children.find(c => c.childId === enhChild.childId || c.id === enhChild.childId);
          loadHeartDatesForChild(enhChild.childId, monthStart, monthEnd, fullChild?.familyTimezone);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enhancedDashboard, selectedFamily]);

  // Subscribe to real-time events from SignalR and refresh relevant data
  useEffect(() => {
    const unsubProgress = on('DailyProgressUpdated', (data: unknown) => {
      const progress = data as {
        childId: number;
        starCount: number;
        triangleCount: number;
        squareCount: number;
        flipYourCoinCount: number;
        makeItRightCount: number;
        oneOnOneTimeCount: number;
        resetCount?: number;
        currentPosition: number;
        currentPositionDisplayName: string;
        onTrackForHeartToday: boolean;
      };
      setEnhancedDashboard(current => {
        if (!current) return current;
        return {
          ...current,
          children: current.children.map(child =>
            child.childId === progress.childId
              ? {
                  ...child,
                  todaysBehaviorCounts: {
                    starCount: progress.starCount,
                    triangleCount: progress.triangleCount,
                    squareCount: progress.squareCount,
                    flipYourCoinCount: progress.flipYourCoinCount,
                    makeItRightCount: progress.makeItRightCount,
                    oneOnOneTimeCount: progress.oneOnOneTimeCount,
                    resetCount: progress.resetCount ?? child.todaysBehaviorCounts.resetCount,
                  },
                  ladderPosition: {
                    ...child.ladderPosition,
                    currentPosition: progress.currentPosition,
                    currentPositionDisplayName: progress.currentPositionDisplayName,
                  },
                  onTrackForHeartToday: progress.onTrackForHeartToday,
                }
              : child
          ),
        };
      });
      setTimelineRefreshKey(prev => prev + 1);
    });
    const unsubBehaviorRecorded = on('BehaviorRecorded', (_data: unknown) => {
      // A new behavior was recorded — refresh the timeline to show it
      setTimelineRefreshKey(prev => prev + 1);
    });
    const unsubJourneyUpdated = on('JourneyUpdated', (data: unknown) => {
      const journey = data as { childId: number; position: number; positionDisplayName: string };
      setEnhancedDashboard(current => {
        if (!current) return current;
        return {
          ...current,
          children: current.children.map(child =>
            child.childId === journey.childId
              ? {
                  ...child,
                  ladderPosition: {
                    ...child.ladderPosition,
                    currentPosition: journey.position,
                    currentPositionDisplayName: journey.positionDisplayName,
                  },
                }
              : child
          ),
        };
      });
    });
    const unsubGoalUpdated = on('GoalUpdated', () => {
      loadEnhancedDashboard();
      loadGoalsReadyToCelebrate();
    });
    const unsubGoalDeleted = on('GoalDeleted', () => {
      loadEnhancedDashboard();
      loadGoalsReadyToCelebrate();
    });
    const unsubChoreList = on('ChoreListUpdated', () => {
      if (selectedFamilyRef.current) {
        loadEnhancedDashboard();
      }
    });
    const unsubChoreListDeleted = on('ChoreListDeleted', (data: unknown) => {
      const payload = data as { childId: number; choreListId: number };
      setEnhancedDashboard(current => {
        if (!current) return current;
        return {
          ...current,
          children: current.children.map(child =>
            child.childId === payload.childId
              ? {
                  ...child,
                  choreStatuses: child.choreStatuses.filter(s => s.id !== payload.choreListId),
                }
              : child
          ),
        };
      });
    });
    const unsubChoreAwaiting = on('ChoreAwaitingApproval', (data: unknown) => {
      const payload = data as { childId: number; pendingLists: ChoreListPeriodStatus[] };
      setEnhancedDashboard(current => {
        if (!current) return current;
        return {
          ...current,
          children: current.children.map(child => {
            if (child.childId !== payload.childId) return child;
            const others = child.choreStatuses.filter(
              s => !(s.type === ChoreListType.Regular && s.requiresApproval && s.status === ChoreListStatus.PendingApproval)
            );
            return { ...child, choreStatuses: [...others, ...payload.pendingLists] };
          }),
        };
      });
    });
    const unsubMIR = on('MakeItRightUpdated', (data: unknown) => {
      const payload = data as { childId: number; makeItRightLists: ChoreListPeriodStatus[] };
      setEnhancedDashboard(current => {
        if (!current) return current;
        return {
          ...current,
          children: current.children.map(child =>
            child.childId !== payload.childId ? child : {
              ...child,
              choreStatuses: mergeChoreStatusesByType(child.choreStatuses, payload.makeItRightLists, ChoreListType.MakeItRight),
            }
          ),
        };
      });
    });
    const unsubRAR = on('ResetAndReflectUpdated', (data: unknown) => {
      const payload = data as { childId: number; resetAndReflectLists: ChoreListPeriodStatus[] };
      setEnhancedDashboard(current => {
        if (!current) return current;
        return {
          ...current,
          children: current.children.map(child =>
            child.childId !== payload.childId ? child : {
              ...child,
              choreStatuses: mergeChoreStatusesByType(child.choreStatuses, payload.resetAndReflectLists, ChoreListType.ResetAndReflect),
            }
          ),
        };
      });
    });
    const unsubOOO = on('OneOnOneTimeUpdated', (data: unknown) => {
      const payload = data as { childId: number; oneOnOneTimeLists: ChoreListPeriodStatus[] };
      setEnhancedDashboard(current => {
        if (!current) return current;
        return {
          ...current,
          children: current.children.map(child =>
            child.childId !== payload.childId ? child : {
              ...child,
              choreStatuses: mergeChoreStatusesByType(child.choreStatuses, payload.oneOnOneTimeLists, ChoreListType.OneOnOneTime),
            }
          ),
        };
      });
    });
    const unsubGoalReached = on('GoalReached', () => {
      loadGoalsReadyToCelebrate();
    });
    const unsubChild = on('ChildUpdated', () => loadEnhancedDashboard());
    const unsubFamily = on('FamilyUpdated', () => loadEnhancedDashboard());
    const unsubPeriodReset = on('PeriodReset', () => loadEnhancedDashboard());

    return () => {
      unsubProgress();
      unsubBehaviorRecorded();
      unsubJourneyUpdated();
      unsubGoalUpdated();
      unsubGoalDeleted();
      unsubChoreList();
      unsubChoreListDeleted();
      unsubChoreAwaiting();
      unsubMIR();
      unsubRAR();
      unsubOOO();
      unsubGoalReached();
      unsubChild();
      unsubFamily();
      unsubPeriodReset();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);


  // Issue #1520: Load heart dates for a specific child's date range
  const loadHeartDatesForChild = useCallback(async (
    childId: number,
    startDate: Date,
    endDate: Date,
    familyTimezone?: string
  ) => {
    setChildCalendarState(prev => {
      if (prev[childId]) {
        return { ...prev, [childId]: { ...prev[childId], heartLoading: true } };
      }
      const todayStr = getTodayInFamilyTimezone(familyTimezone);
      const [y, m, d] = todayStr.split('-').map(Number);
      return {
        ...prev,
        [childId]: {
          heartDates: [],
          mendingHeartDates: [],
          heartLoading: true,
          selectedDate: todayStr,
          selectedCalendarDate: new Date(y, m - 1, d),
          dailyProgress: [],
        },
      };
    });
    try {
      const result = await behaviorAPI.getChildProgressHistorySummary(childId, startDate, endDate, familyTimezone);
      const { heartDates, mendingHeartDates } = deriveHeartDates(result);
      setChildCalendarState(prev => ({
        ...prev,
        [childId]: { ...prev[childId], heartDates, mendingHeartDates, heartLoading: false, dailyProgress: result },
      }));
    } catch (err) {
      console.error(`Failed to load progress history for child ${childId}:`, err);
      setChildCalendarState(prev => ({
        ...prev,
        [childId]: { ...prev[childId], heartDates: [], mendingHeartDates: [], heartLoading: false, dailyProgress: [] },
      }));
    }
  }, []);

  // Issue #1520: Handle calendar day selection for a specific child
  const handleChildCalendarDaySelect = useCallback((childId: number, familyTimezone?: string) => async (date: Date) => {
    const dateStr = getDateInFamilyTimezone(date, familyTimezone);
    setChildCalendarState(prev => ({
      ...prev,
      [childId]: { ...prev[childId], selectedDate: dateStr, selectedCalendarDate: date },
    }));

    // Fetch historical chore statuses when not viewing today (Issue #1740)
    const todayStr = getTodayInFamilyTimezone(familyTimezone);
    const family = selectedFamilyRef.current;
    if (dateStr !== todayStr && family) {
      try {
        const statuses = await choresAPI.getChoreListStatuses(family.familyId, childId, dateStr);
        setHistoricalChildChoreStatuses(prev => ({ ...prev, [childId]: statuses }));
      } catch {
        setHistoricalChildChoreStatuses(prev => ({ ...prev, [childId]: [] }));
      }
    } else {
      // Returning to today: remove historical entry for this child
      setHistoricalChildChoreStatuses(prev => {
        const next = { ...prev };
        delete next[childId];
        return next;
      });
    }
  }, []);

  // Issue #1520: Handle calendar month navigation for a specific child
  const handleChildCalendarDateRangeChange = useCallback((childId: number, familyTimezone?: string) => (start: Date, end: Date) => {
    loadHeartDatesForChild(childId, start, end, familyTimezone);
  }, [loadHeartDatesForChild]);

  // Sets the highlighted chore list and starts its 2s auto-clear timer.
  // Cancels any pending timer from a previous highlight first, so a fast second
  // trigger never gets cut short by a leftover timer from the first (issue #1907).
  const triggerHighlight = useCallback((choreListId: number | null) => {
    if (highlightClearTimerRef.current) {
      clearTimeout(highlightClearTimerRef.current);
      highlightClearTimerRef.current = null;
    }
    setHighlightedListId(choreListId);
    if (choreListId != null) {
      highlightClearTimerRef.current = setTimeout(() => {
        setHighlightedListId(null);
        highlightClearTimerRef.current = null;
      }, 2000);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (highlightClearTimerRef.current) {
        clearTimeout(highlightClearTimerRef.current);
      }
    };
  }, []);

  // Scrolls to the chore list targeted by an approval-queue navigation, once the
  // child's tab has actually rendered (activeTab dependency ensures this runs after
  // the tab-switch commit, not before). If the list no longer exists (e.g. another
  // parent already approved it), this silently no-ops — no toast, no error (issue #1907).
  const [scrollTargetListId, setScrollTargetListId] = useState<number | null>(null);
  useEffect(() => {
    if (scrollTargetListId == null) return;
    const el = document.getElementById(`chore-list-${scrollTargetListId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setScrollTargetListId(null);
  }, [scrollTargetListId, activeTab]);

  // Handle chore item toggle from parent dashboard
  const handleChoreItemToggle = async (choreListId: number, itemId: number, isCurrentlyComplete: boolean, childId: number) => {
    try {
      if (isCurrentlyComplete) {
        await choresAPI.markItemIncomplete(choreListId, itemId);
        setSnackbar({
          open: true,
          message: 'Item marked incomplete',
          severity: 'success',
        });
      } else {
        const result = await choresAPI.markItemComplete(choreListId, itemId);

        // Get the chore list to check if it's MakeItRight
        const choreList = childChoreStatuses[childId]?.find(c => c.id === choreListId);
        const isMakeItRight = choreList?.type === ChoreListType.MakeItRight;

        // Check if star was awarded for completing all items
        if (result.starAwarded && result.choreListTitle) {
          triggerHighlight(result.choreListId ?? null);

          if (isMakeItRight) {
            // MakeItRight lists need parent approval, not a star
            celebrate({
              message: `Great job! ${result.choreListTitle} complete - ready for parent approval!`,
              onToast: (msg) => setSnackbar({ open: true, message: msg, severity: 'success' }),
            });
          } else {
            celebrate({
              message: `Great job! ${result.choreListTitle} complete - you earned a star!`,
              onToast: (msg) => setSnackbar({ open: true, message: msg, severity: 'success' }),
            });
          }
          // Trigger auto-collapse for this list after celebration (Issue #1515)
          setAutoCollapseListIds(prev => new Set(prev).add(choreListId));
        } else {
          setSnackbar({
            open: true,
            message: 'Item completed!',
            severity: 'success',
          });
        }
      }

      // Refresh dashboard to update chore statuses and star counts (Issue #1369, #1742)
      // Use the returned data directly — reading from the ref would be stale since React
      // state updates are asynchronous and the ref won't reflect the new value until after
      // the next render (Issue #1742 review).
      const freshDashboard = await loadEnhancedDashboard();

      // Remove auto-collapse IDs for lists that are now incomplete (Issue #1515)
      const refreshedStatuses = freshDashboard?.children
        .find(c => c.childId === childId)?.choreStatuses ?? [];
      setAutoCollapseListIds(prev => {
        const stillComplete = new Set(refreshedStatuses.filter(c => c.isComplete).map(c => c.id));
        return new Set([...prev].filter(id => stillComplete.has(id)));
      });
    } catch (error) {
      console.error('Error toggling chore item:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update item',
        severity: 'error',
      });
    }
  };

  const loadEnhancedDashboard = async () => {
    try {
      // Load families first to get the timezone
      const familiesData = await familyAPI.getFamilies();
      setFamilies(familiesData);
      if (familiesData.length > 0 && !selectedFamily) {
        setSelectedFamily(familiesData[0]);
      }
      
      // Get family timezone for consistent date calculations (Issue #312)
      const familyTimezone = familiesData.length > 0 ? familiesData[0].timezone : undefined;
      
      // Use family timezone for consistency with behavior recording (Issue #312)
      const dashboardParams = { 
        familyTimezone: familyTimezone || 'America/Chicago' 
      };
      
      const dashboardData = await familyAPI.getEnhancedDashboard(dashboardParams);
      setEnhancedDashboard(dashboardData);

      setError(null);
      return dashboardData;
    } catch (error) {
      console.error('Failed to load enhanced dashboard:', error);

      // If we get a 404 "No family found", redirect to family setup
      if (axios.isAxiosError(error) && error.response?.status === 404 && error.response?.data?.message?.includes('No family found')) {
        navigate(`${basePath}-setup`, { replace: true });
        return;
      }

      // 401 errors are now handled by the global interceptor
      setError(`Failed to load dashboard: ${error}`);
      showSnackbar('Failed to load dashboard', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadFamilies = async () => {
    try {
      const familiesData = await familyAPI.getFamilies();
      setFamilies(familiesData);
      if (familiesData.length > 0 && !selectedFamily) {
        setSelectedFamily(familiesData[0]);
      }
      setError(null);
    } catch (error) {
      console.error('Failed to load families:', error);

      // If 401, the token is likely expired - redirect to login
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/';
        return;
      }
      
      setError(`Failed to load families: ${error}`);
      showSnackbar('Failed to load families', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadChildren = async (familyId: number) => {
    try {
      const childrenData = await familyAPI.getChildren(familyId);
      setChildren(childrenData);
    } catch (error) {
      console.error('Failed to load children:', error);
      showSnackbar('Failed to load children', 'error');
    }
  };

  const loadGoalsReadyToCelebrate = async () => {
    if (!selectedFamily) return;

    // Issue #1767: celebrations are per-child. On the "All Kids" tab there is no active
    // child, so there is nothing to celebrate here — clear the list and skip the fetch.
    if (activeChildId == null) {
      setGoalsReadyToCelebrate([]);
      setTotalCelebrateCount(0);
      return;
    }

    try {
      const response = await goalsAPI.getFamilyGoalsPaginated(selectedFamily.familyId, {
        filterStatus: 'completed',
        childId: activeChildId,
        page: celebratePage + 1, // API uses 1-based pages
        // nalu-v1 shows every celebration-ready goal at once, no pagination UI (issue #1900) —
        // a large fixed page size in place of celebrateRowsPerPage, which stays untouched for
        // the other themes' existing paginated UI.
        pageSize: isNaluV1Theme ? 100 : celebrateRowsPerPage,
      });

      // Handle empty page after celebration — decrement page
      if (response.data.length === 0 && celebratePage > 0) {
        setTotalCelebrateCount(response.totalCount);
        setCelebratePage(p => p - 1);
        return;
      }

      setGoalsReadyToCelebrate(response.data);
      setTotalCelebrateCount(response.totalCount);
      hasLoadedCelebrateOnce.current = true;
    } catch (error) {
      console.error('Failed to load family goals:', error);
      // Silent failure for goals ready to celebrate
    }
  };


  const handleAppBarMenuClick = (action: string) => {
    if (action === '/event-log') {
      navigate('/event-log');
    } else if (action === 'Dashboard') {
      // Already on dashboard
    } else {
      showSnackbar(`${action} page coming soon!`, 'success');
    }
  };

  // Issue #2012: a parent-recorded Reset has no ladder or count effect, so it skips the optimistic
  // update; it records and, when the server produced a crossing, takes the parent straight into it.
  const startParentReset = async (childId: number | string) => {
    try {
      const response = await behaviorAPI.recordBehavior(Number(childId), BehaviorType.Reset);
      if (typeof response.crossingId === 'number') {
        navigate(`/crossing/${response.crossingId}`, { state: { returnToChildId: childId } });
        return;
      }
      console.error('Reset recorded but no crossing was returned for child', childId);
    } catch (error) {
      console.error('Failed to record Reset:', error);
    }
    showSnackbar('Could not start the Reset. Please try again.', 'error');
  };

  const handleBehaviorClick = async (childId: number | string, behaviorType: 'star' | 'triangle' | 'square' | BehaviorType) => {
    if (!selectedFamily || !enhancedDashboard) return;

    // Away day warning: prompt before recording (issue #1706)
    if (isTodayAwayDay && !awayDayConfirmPending && !awayDayDismissedForToday) {
      setAwayDayConfirmPending({ childId, behaviorType });
      return;
    }
    // Clear the pending state now that we're proceeding
    setAwayDayConfirmPending(null);

    if (behaviorType === BehaviorType.Reset) {
      await startParentReset(childId);
      return;
    }

    // Store original dashboard state for potential rollback
    const originalDashboard = enhancedDashboard;
    
    try {
      let apiBehaviorType: 'positive' | 'warning' | 'negative' | BehaviorType;
      
      // Map UI behavior types to API types (support both old string types and new enum)
      if (typeof behaviorType === 'string') {
        const behaviorTypeMap = {
          'star': 'positive' as const,
          'triangle': 'warning' as const, 
          'square': 'negative' as const
        };
        apiBehaviorType = behaviorTypeMap[behaviorType];
      } else {
        // Direct BehaviorType enum (e.g., BehaviorType.FlipYourCoin)
        apiBehaviorType = behaviorType;
      }
      
      // Map behavior types to their corresponding count field names
      const behaviorCountMap: Record<string, keyof typeof enhancedDashboard.children[0]['todaysBehaviorCounts']> = {
        'star': 'starCount',
        'triangle': 'triangleCount',
        'square': 'squareCount',
        [BehaviorType.FlipYourCoin]: 'flipYourCoinCount',
        [BehaviorType.OneOnOneTime]: 'oneOnOneTimeCount',
        [BehaviorType.MakeItRight]: 'makeItRightCount',
      };

      const countField = behaviorCountMap[behaviorType as string];

      // Optimistic update - show expected result immediately
      const optimisticDashboard = {
        ...enhancedDashboard,
        children: enhancedDashboard.children.map(child => {
          if (child.childId === Number(childId)) {
            // Issue #1415: For star behaviors, also increment StarCollection goal progress
            const updatedActiveGoals = behaviorType === 'star'
              ? child.activeGoals.map(goal => {
                  if (goal.goalType === GoalType.StarCollection) {
                    return {
                      ...goal,
                      currentValue: goal.currentValue + 1,
                      progressText: `${goal.currentValue + 1}/${goal.targetValue}`,
                    };
                  }
                  return goal;
                })
              : child.activeGoals;

            return {
              ...child,
              todaysBehaviorCounts: {
                ...child.todaysBehaviorCounts,
                // Dynamically increment the appropriate count field
                ...(countField && { [countField]: child.todaysBehaviorCounts[countField] + 1 }),
              },
              activeGoals: updatedActiveGoals,
            };
          }
          return child;
        })
      };

      // Apply optimistic update
      setEnhancedDashboard(optimisticDashboard);
      
      const response = await behaviorAPI.recordBehavior(
        Number(childId),
        apiBehaviorType
      );
      
      // Update with actual server response (this is the authoritative data)
      if (response.ladderUpdate) {
        // Use functional state update to ensure we're working with the current state
        setEnhancedDashboard(currentDashboard => {
          if (!currentDashboard) return currentDashboard;
          
          return {
            ...currentDashboard,
            children: currentDashboard.children.map(child => {
              if (child.childId === Number(childId)) {
                return {
                  ...child,
                  todaysBehaviorCounts: {
                    starCount: response.ladderUpdate.currentStarCount,
                    triangleCount: response.ladderUpdate.currentTriangleCount,
                    squareCount: response.ladderUpdate.currentSquareCount,
                    flipYourCoinCount: response.ladderUpdate.currentFlipYourCoinCount || 0,
                    makeItRightCount: response.ladderUpdate.currentMakeItRightCount || 0,
                    oneOnOneTimeCount: response.ladderUpdate.currentOneOnOneTimeCount || 0,
                    // The tally response carries no Reset count, so keep the one already shown.
                    resetCount: child.todaysBehaviorCounts.resetCount,
                  },
                  ladderPosition: {
                    ...child.ladderPosition,
                    currentPosition: response.ladderUpdate.newPosition,
                    currentPositionDisplayName: response.ladderUpdate.newPositionDisplayName,
                  }
                };
              }
              return child;
            })
          };
        });
      }

      // Refresh the timeline chart so it reflects the newly recorded behavior
      setTimelineRefreshKey(prev => prev + 1);

      // Issue #1414: Apply goal progress updates from star behavior response
      // This enables real-time goal updates without page refresh
      if (response.ladderUpdate.updatedGoals?.length) {
        // Check if any goals were just completed
        const completedGoalIds = response.ladderUpdate.updatedGoals
          .filter(g => g.status === GoalStatus.Completed || g.status === GoalStatus.Celebrated)
          .map(g => g.goalId);

        // Check if there are new goals we don't have (e.g., newly created recurring goals)
        const currentGoalIds = enhancedDashboard?.children
          .find(c => c.childId === Number(childId))
          ?.activeGoals.map(g => g.goalId) ?? [];
        const hasNewGoals = response.ladderUpdate.updatedGoals.some(
          g => g.status === GoalStatus.Active && !currentGoalIds.includes(g.goalId)
        );

        if (completedGoalIds.length > 0 || hasNewGoals) {
          // Goals completed or new recurring goals created - refresh dashboard to get full goal data
          loadGoalsReadyToCelebrate();
          loadEnhancedDashboard();
        } else {
          // Just update progress for existing goals
          setEnhancedDashboard(currentDashboard => {
            if (!currentDashboard) return currentDashboard;

            return {
              ...currentDashboard,
              children: currentDashboard.children.map(child => {
                if (child.childId === Number(childId)) {
                  return {
                    ...child,
                    activeGoals: child.activeGoals.map(goal => {
                      const update = response.ladderUpdate.updatedGoals?.find(
                        u => u.goalId === goal.goalId
                      );
                      if (update) {
                        return {
                          ...goal,
                          currentValue: update.currentProgress,
                          progressPercentage: Math.round(
                            (update.currentProgress / update.targetValue) * 100
                          ),
                        };
                      }
                      return goal;
                    }),
                  };
                }
                return child;
              }),
            };
          });
        }
      }

      // Issue #1415: Handle newly completed goals from star recording
      if (response.newlyCompletedGoals && response.newlyCompletedGoals.length > 0) {
        // Add each completed goal to the celebrating state
        setCelebratingGoals((prev) => {
          const next = new Map(prev);
          response.newlyCompletedGoals!.forEach((goal) => {
            const key = `${goal.childId}-${goal.goalId}`;
            next.set(key, goal);

            // Trigger celebration with confetti and toast
            celebrate({
              message: `Goal complete: ${goal.goalTitle}!`,
              onToast: (msg) => showSnackbar(msg, 'success'),
            });
          });
          return next;
        });

        // Refresh dashboard to get new recurring goals with their correct star counts
        // This ensures the new goal appears at the bottom with the star already counted
        loadEnhancedDashboard();
      } else {
        // Only show the default success message if no goals were completed
        // Issue #1441: Include auto-square notification when triangle hits ladder bottom
        const autoSquareNote = response.ladderUpdate?.autoSquareTriggered
          ? ' A square was also auto-triggered for hitting the bottom of the ladder.'
          : '';
        // Issue #1627: Resolve human-readable name for all behavior types (string shorthand and BehaviorType enum)
        const stringToBehaviorTypeForDisplay: Record<string, BehaviorType> = {
          'star': BehaviorType.Star,
          'triangle': BehaviorType.Triangle,
          'square': BehaviorType.Square,
        };
        const displayBehaviorType = typeof behaviorType === 'string'
          ? stringToBehaviorTypeForDisplay[behaviorType]
          : behaviorType;
        const displayName = getBehaviorLabel(displayBehaviorType);
        showSnackbar(`${displayName} recorded successfully!${autoSquareNote}`, 'success');
      }
    } catch (error) {
      console.error('Failed to record behavior:', error);
      
      // Roll back optimistic update on error
      setEnhancedDashboard(originalDashboard);
      
      showSnackbar('Failed to record behavior', 'error');
    }
  };

  const handleMicrophonePress = () => {
    setIsRecording(true);
    // TODO: Implement voice recording
  };

  const handleMicrophoneRelease = () => {
    setIsRecording(false);
    // TODO: Process voice command
    showSnackbar('Voice recording feature coming soon!', 'success');
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCelebrateGoal = (goal: Goal) => {
    const child = children.find(c => (c.childId || c.id) === goal.childId);
    const childName = child?.name || 'Unknown Child';
    setCelebrationModal({ isOpen: true, goal, childName });
  };

  const handleGoalCelebrated = (_celebratedGoal: Goal) => {
    setCelebrationModal({ isOpen: false, goal: null, childName: '' });
    showSnackbar('Goal celebrated successfully!', 'success');
    // Refetch current page from server to get updated data
    loadGoalsReadyToCelebrate();
  };

  // Nuclear reset handler (Issue #1310)
  const handleNukeConfirm = async () => {
    if (nukeState.childId === null) return;
    setNukeState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await goalsAPI.nukeChildGoals(nukeState.childId);
      setNukeState({ childId: null, loading: false, error: null });
      badCelebration();
      // Reload dashboard data so goal list refreshes
      loadEnhancedDashboard();
      showSnackbar('Nuclear reset complete — all goals have been restarted from zero.', 'success');
    } catch {
      setNukeState(prev => ({ ...prev, loading: false, error: 'Nuclear reset failed. Please try again.' }));
    }
  };

  const getGoalTypeIcon = (type: GoalType, fontSize = 16) => {
    switch (type) {
      case GoalType.HeartStreak:
        return <HeartIcon data-testid="heart-streak-icon" sx={{ color: theme.palette.behaviorHeart, fontSize }} />;
      case GoalType.StarCollection:
        return <StarIcon data-testid="star-collection-icon" sx={{ color: starAccent, fontSize }} />;
      case GoalType.HeartCollection:
        return <HeartIcon data-testid="heart-collection-icon" sx={{ color: theme.palette.behaviorHeart, fontSize }} />;
      default:
        return <TrophyIcon sx={{ color: theme.palette.secondary.main, fontSize }} />;
    }
  };

  // Border/button accent per goal type — matches the mockup's per-card coloring on the
  // Ready to Celebrate row (issue #1900). Reuses the same type grouping as getGoalTypeIcon.
  const getGoalCelebrationAccent = (type: GoalType) => {
    switch (type) {
      case GoalType.HeartStreak:
      case GoalType.HeartCollection:
        return { border: alpha(theme.palette.behaviorHeart, 0.6), button: theme.palette.behaviorHeart };
      case GoalType.StarCollection:
        return { border: theme.palette.primary.light, button: theme.palette.primary.dark };
      default:
        return { border: theme.palette.secondary.light, button: theme.palette.secondary.main };
    }
  };

  const getChildName = (childId: number): string => {
    const child = children.find(c => (c.childId || c.id) === childId);
    return child?.name || 'Unknown Child';
  };



  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Loading...</Typography>
      </Box>
    );
  }


  // Show error state
  if (error) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" p={4}>
        <Typography variant="h5" color="error" gutterBottom>
          Something went wrong
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" mb={2}>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => {
          setError(null);
          setIsLoading(true);
          loadFamilies();
        }}>
          Try Again
        </Button>
      </Box>
    );
  }

  // Show family setup wizard if requested
  if (showFamilySetup) {
    return <FamilySetupWizard />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Book-art sky behind the page (issue #1900) — fixed so content scrolls over it,
          not with it; matches the mockups' own sky-wash technique (a full-viewport
          layer behind everything, rather than background-attachment: fixed on the body,
          which has known repaint quirks on mobile Safari). Nalu-v1 only. */}
      {isNaluV1Theme && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: -1,
            background: 'url(/nalu/bg-morning-37.png) center / cover no-repeat',
          }}
        />
      )}
      <SharedAppBar
        title="Let's Reset"
        onMenuClick={handleAppBarMenuClick}
      />

      {/* Main Container */}
      <Container
        maxWidth="xl"
        disableGutters
        data-testid="dashboard-main-container"
        sx={{
          flexGrow: 1,
          pt: 3,
          pb: 1,
          px: { xs: 1, sm: 2 }
        }}
      >
        {/* Make It Right Approval Ribbon */}
        <MakeItRightApprovalRibbon childChoreStatuses={childChoreStatuses} />

        {/* Reset and Reflect Approval Ribbon */}
        <ResetAndReflectApprovalRibbon childChoreStatuses={childChoreStatuses} />

        {/* Responsibilities Approval Ribbon */}
        <ResponsibilitiesApprovalRibbon childChoreStatuses={childChoreStatuses} />

        <>
          {/* Away Day Banner (issue #1706) — family-wide, above the tabs */}
          {isTodayAwayDay && (
            <Alert
              severity="info"
              icon={<FlightTakeoffIcon />}
              data-testid="away-day-banner"
              sx={{ mb: 2 }}
            >
              Today is marked as an away day. Hearts, streaks, and chores won&apos;t be penalized — but any behaviors you record will still count.
            </Alert>
          )}

          {/* Dashboard tabs (issue #1767): All Kids quick-record + one full tab per child */}
          <Box sx={{ borderBottom: isNaluV1Theme ? 0 : 1, borderColor: 'divider', mb: isNaluV1Theme ? 3 : 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Family dashboard tabs"
            >
              <Tab label="All Kids" {...dashboardA11yProps(0)} />
              {enhancedDashboard?.children.map((child, i) => (
                <Tab key={child.childId} label={child.name} {...dashboardA11yProps(i + 1)} />
              ))}
            </Tabs>
          </Box>

          {/* Tab 0: All Kids quick-record (6 inputs + timeline per child) */}
          <DashboardTabPanel value={activeTab} index={0}>
            {!enhancedDashboard || enhancedDashboard.children.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Typography variant="h5" gutterBottom>
                  No children in your family yet
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setShowFamilySetup(true)}
                  sx={{ mt: 2 }}
                >
                  Add Family Members
                </Button>
              </Box>
            ) : (
              <>
                <FamilyStrip
                  familyId={selectedFamily?.familyId ?? 0}
                  familyChildren={enhancedDashboard.children}
                  onNavigateToChild={(childId, choreListId) => {
                    const idx = enhancedDashboard.children.findIndex(c => c.childId === childId);
                    if (idx >= 0) setActiveTab(idx + 1);
                    setScrollTargetListId(choreListId);
                    triggerHighlight(choreListId);
                  }}
                  onApprovalChanged={loadEnhancedDashboard}
                />
                <AllKidsQuickRecordTab
                  enhancedChildren={enhancedDashboard.children}
                  fullChildren={children}
                  familyId={selectedFamily?.familyId ?? 0}
                  timelineRefreshKey={timelineRefreshKey}
                  childHeartState={childCalendarState}
                  onBehaviorClick={(childId, behaviorType) => handleBehaviorClick(childId, behaviorType)}
                  crossingEnabled={siteSettings?.crossingEnabled ?? false}
                  onChoreItemToggle={(childId, listId, itemId, isComplete) =>
                    handleChoreItemToggle(listId, itemId, isComplete, childId)
                  }
                />
                {siteSettings?.crossingEnabled && (
                  <CrossingListAllKids
                    childIds={enhancedDashboard.children.map(c => c.childId)}
                    childNameById={Object.fromEntries(enhancedDashboard.children.map(c => [c.childId, c.name]))}
                    familyTimezone={selectedFamily?.timezone}
                  />
                )}
              </>
            )}
          </DashboardTabPanel>

          {/* Tabs 1..N: full per-child view (unchanged single-child dashboard) */}
          {enhancedDashboard?.children.map((child, index) => {
              const fullChild = children.find(c => c.childId === child.childId || c.id === child.childId);
              const calState = childCalendarState[child.childId];
              return (
              <DashboardTabPanel key={child.childId} value={activeTab} index={index + 1}>
              <Paper
                elevation={2}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  bgcolor: getChildColor(index),
                  border: `1px solid ${getChildBorderColor(index)}`, // matches the All Kids tab's per-child card treatment (issue #1900)
                  borderRadius: '24px',
                  py: '28px',
                  px: siteSettings?.crossingEnabled ? { xs: '16px', sm: '32px' } : '32px',
                  minHeight: 160, // Increased height for enhanced content
                }}
              >
                {/* Child Name Header with Behavior Buttons aligned to top */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2,
                  // With crossings on the four-button group wraps onto its own line below sm.
                  ...(siteSettings?.crossingEnabled && { flexWrap: 'wrap', columnGap: 1.5 }),
                }}>
                  <Box
                    onClick={() => navigate(`/child/${child.childId}`)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      ...(siteSettings?.crossingEnabled && { flex: '1 1 auto', minWidth: 0 }),
                    }}
                  >
                    <Avatar sx={{ width: 52, height: 52, bgcolor: getChildBorderColor(index), color: 'common.white', fontWeight: 700 }}>
                      {child.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontSize: '22px', fontWeight: 600, lineHeight: 1, '&:hover': { textDecoration: 'underline' } }}>
                        {child.name}
                      </Typography>
                      {fullChild && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '12.5px' }}>
                          Age {fullChild.age ?? new Date().getFullYear() - fullChild.birthYear}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Behavior Buttons with Count Badges - Top aligned and closer together */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    gap: { xs: 1, sm: 1.5 },
                    ...(siteSettings?.crossingEnabled && {
                      order: { xs: 3, sm: 0 },
                      flexBasis: { xs: '100%', sm: 'auto' },
                      flexShrink: 0,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                    }),
                  }}>
                    {behaviorTypes.map((behavior) => {
                      const count = behavior.type === 'star' ? child.todaysBehaviorCounts.starCount :
                                   behavior.type === 'triangle' ? child.todaysBehaviorCounts.triangleCount :
                                   child.todaysBehaviorCounts.squareCount;

                      return (
                        <BehaviorTallyButton
                          key={behavior.type}
                          behaviorType={behavior.type}
                          icon={behavior.icon}
                          count={count}
                          onClick={() => handleBehaviorClick(child.childId, behavior.type)}
                          wrapCaption={!!siteSettings?.crossingEnabled}
                        />
                      );
                    })}
                    {siteSettings?.crossingEnabled && (
                      <ParentResetButton
                        child={child}
                        count={child.todaysBehaviorCounts.resetCount}
                        onReset={(childId) => handleBehaviorClick(childId, BehaviorType.Reset)}
                      />
                    )}
                  </Box>

                  {/* Dashboard Navigation Arrow */}
                  <IconButton
                    onClick={() => navigate(`/child/${child.childId}`)}
                    sx={{
                      ...(siteSettings?.crossingEnabled && { width: 44, height: 44 }),
                      bgcolor: 'white',
                      '&:hover': {
                        bgcolor: 'grey.100',
                      },
                    }}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </Box>

                {!siteSettings?.crossingEnabled && (
                  <>
                    {/* Responsive Layout: Mobile vertical, Desktop horizontal */}
                    {/* Mobile/Tablet Layout (below md): Buttons in row, Ladder below */}
                    <Box sx={{
                      display: { xs: 'block', md: 'none' },
                      mt: 1,
                      mb: 1
                    }}>
                      {/* Mobile: FlipCoin, MakeItRight, and 1:1 Time Buttons */}
                      <Box
                        data-testid="flip-coin-make-it-right-container"
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          flexWrap: 'wrap',
                          justifyContent: 'center',
                          pl: 0,
                          mb: 1,
                          gap: 1
                        }}
                      >
                        <BehaviorCountBadge count={child.todaysBehaviorCounts.flipYourCoinCount} color="primary">
                          <FlipYourCoinButton
                            child={child}
                            onBehaviorRecorded={(behaviorType) => handleBehaviorClick(child.childId, behaviorType)}
                          />
                        </BehaviorCountBadge>
                        <BehaviorCountBadge count={child.todaysBehaviorCounts.makeItRightCount} color="secondary">
                          <MakeItRightButton
                            child={child}
                            isParentView={true}
                            dailySquareCount={child.todaysBehaviorCounts.squareCount}
                            dailyMakeItRightCount={child.todaysBehaviorCounts.makeItRightCount}
                            onBehaviorRecorded={(behaviorType) => handleBehaviorClick(child.childId, behaviorType)}
                          />
                        </BehaviorCountBadge>
                        <BehaviorCountBadge count={child.todaysBehaviorCounts.oneOnOneTimeCount} color="primary">
                          <OneOnOneTimeButton
                            child={child}
                            onBehaviorRecorded={(behaviorType) => handleBehaviorClick(child.childId, behaviorType)}
                          />
                        </BehaviorCountBadge>
                      </Box>
                  
                    </Box>

                    {/* Desktop Layout (md+): FlipCoin | MakeItRight | 1:1 Time horizontal */}
                    <Box
                      data-testid="desktop-horizontal-layout"
                      sx={{
                        display: { xs: 'none', md: 'flex' },
                        justifyContent: 'center',
                        alignItems: 'center',
                        mt: 1,
                        mb: 1,
                        gap: 3, // Fixed gap between elements
                        px: 2
                      }}
                    >
                      <BehaviorCountBadge count={child.todaysBehaviorCounts.flipYourCoinCount} color="primary">
                        <FlipYourCoinButton
                          child={child}
                          onBehaviorRecorded={(behaviorType) => handleBehaviorClick(child.childId, behaviorType)}
                        />
                      </BehaviorCountBadge>
                      <BehaviorCountBadge count={child.todaysBehaviorCounts.makeItRightCount} color="secondary">
                        <MakeItRightButton
                          child={child}
                          isParentView={true}
                          dailySquareCount={child.todaysBehaviorCounts.squareCount}
                          dailyMakeItRightCount={child.todaysBehaviorCounts.makeItRightCount}
                          onBehaviorRecorded={(behaviorType) => handleBehaviorClick(child.childId, behaviorType)}
                        />
                      </BehaviorCountBadge>
                      <BehaviorCountBadge count={child.todaysBehaviorCounts.oneOnOneTimeCount} color="primary">
                        <OneOnOneTimeButton
                          child={child}
                          onBehaviorRecorded={(behaviorType) => handleBehaviorClick(child.childId, behaviorType)}
                        />
                      </BehaviorCountBadge>
                    </Box>
                  </>
                )}

                {siteSettings?.crossingEnabled && (
                  <CrossingListForChild childId={child.childId} familyTimezone={selectedFamily?.timezone} />
                )}

                {/* Calendar + Timeline Row — Issue #1520. Plain layout Box, not a card itself
                    (issue #1900) — the calendar and timeline are each their own card below,
                    matching the mockup's two-side-by-side-cards treatment rather than nesting
                    them inside a third outer card. */}
                {calState && (
                  <Box
                    data-testid={`child-calendar-timeline-${child.childId}`}
                    sx={{
                      // Same 3-track grid as the goals/calendar/chores row below, with the
                      // timeline spanning 2 tracks — not an independent "1fr 2fr" 2-column
                      // grid. A 2-column grid has 1 gap; a 3-column grid has 2 gaps; since fr
                      // units divide the width *after* subtracting gaps, those two grids don't
                      // actually produce the same column width even at an equal ratio — the
                      // rows would drift out of alignment. Sharing the exact same track/gap
                      // math is what makes the two rows line up pixel-for-pixel (issue #1900).
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                      gap: '20px', // matches the family dashboard's mini-card gap
                      mt: '20px',
                    }}
                  >
                    {/* Heart Calendar — 1/3 width (1 of 3 tracks) on desktop, full width on
                        mobile. Its single child (the calendar's own Paper) stretches to fill
                        the grid cell's full height via the default grid align-items: stretch
                        (issue #1900). */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', '& > .MuiPaper-root': { flexGrow: 1 } }}>
                      <HeartCalendar
                        heartDates={calState.heartDates}
                        mendingHeartDates={calState.mendingHeartDates}
                        loading={calState.heartLoading}
                        viewMode={isMobile ? 'week' : 'month'}
                        localStorageKey={isMobile ? `heart-calendar-expanded-${child.childId}` : undefined}
                        familyTimezone={fullChild?.familyTimezone}
                        familyCreatedAt={fullChild?.createdAt}
                        selectedDate={calState.selectedCalendarDate}
                        onDaySelect={handleChildCalendarDaySelect(child.childId, fullChild?.familyTimezone)}
                        onDateRangeChange={handleChildCalendarDateRangeChange(child.childId, fullChild?.familyTimezone)}
                        dailyProgress={calState.dailyProgress ?? []}
                        borderColor={getChildBorderColor(index)}
                      />
                    </Box>

                    {/* Daily Timeline — 2/3 width (2 of 3 tracks) on desktop, full width on mobile */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gridColumn: { md: 'span 2' } }}>
                      {fullChild && (
                        <Paper elevation={1} sx={{ p: { xs: 1.5, md: 2 }, border: `1px solid ${getChildBorderColor(index)}`, flexGrow: 1 }}>
                          <AnalyticsTimeChart
                            child={fullChild}
                            selectedDate={calState.selectedDate}
                            familyId={selectedFamily?.familyId ?? 0}
                            familyTimezone={fullChild.familyTimezone}
                            isAdminContext={false}
                            hideTitle={false}
                            refreshKey={timelineRefreshKey}
                          />
                        </Paper>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Goals, Calendar, and Chores - Responsive Layout (extracted component) */}
                {(() => {
                  const tz = fullChild?.familyTimezone;
                  // Compare the already-correct selectedDate string directly instead of
                  // reconverting selectedCalendarDate (a Date object) through family tz —
                  // that reconversion is itself a double-conversion risk whenever
                  // selectedCalendarDate was constructed from local (non-noon-anchored)
                  // components, e.g. by the Return-to-today chip (issue #1881).
                  const isChildViewingHistorical = !!(calState && tz &&
                    calState.selectedDate !== getTodayInFamilyTimezone(tz));
                  const historicalDateLabel = isChildViewingHistorical && calState
                    ? (() => {
                        const [y, m, d] = calState.selectedDate.split('-').map(Number);
                        return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      })()
                    : undefined;
                  return (
                    <>
                      {isChildViewingHistorical && historicalDateLabel && (
                        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                          <Chip
                            data-testid={`viewing-historical-banner-${child.childId}`}
                            label={`Viewing ${historicalDateLabel} — Return to today`}
                            onClick={() => {
                              // Set state directly from the already-correct family-tz string
                              // instead of routing through handleChildCalendarDaySelect, which
                              // would rebuild a browser-local Date and re-convert it back through
                              // getDateInFamilyTimezone — rolling the day backward whenever the
                              // browser's timezone differs from the family's (issue #1881).
                              const todayStr = getTodayInFamilyTimezone(tz);
                              const [ty, tm, td] = todayStr.split('-').map(Number);
                              const todayDate = new Date(ty, tm - 1, td);
                              setChildCalendarState(prev => ({
                                ...prev,
                                [child.childId]: { ...prev[child.childId], selectedDate: todayStr, selectedCalendarDate: todayDate },
                              }));
                              setHistoricalChildChoreStatuses(prev => {
                                const next = { ...prev };
                                delete next[child.childId];
                                return next;
                              });
                            }}
                            color="info"
                            variant="outlined"
                            size="small"
                            sx={{ cursor: 'pointer' }}
                          />
                        </Box>
                      )}
                      <ChildContentRow
                        goals={mergeGoalsForChild(child.childId, child.activeGoals, celebratingGoals)}
                        onGoalFadeComplete={(key) => handleGoalFadeComplete(key)}
                        showNuclearReset={true}
                        onNuclearReset={() => setNukeState({ childId: child.childId, loading: false, error: null })}
                        childId={child.childId}
                        showCalendar={!!(user?.isAdmin && child.hasCalendarSelections)}
                        choreStatuses={isChildViewingHistorical
                          ? (historicalChildChoreStatuses[child.childId] || [])
                          : child.choreStatuses}
                        onChoreItemToggle={(listId, itemId, isComplete) =>
                          handleChoreItemToggle(listId, itemId, isComplete, child.childId)
                        }
                        isParentView={true}
                        compact={true}
                        onChoreRefresh={() => {
                          loadEnhancedDashboard();
                        }}
                        autoCollapseListIds={autoCollapseListIds}
                        onApprovalSuccess={(approvedListId) => {
                          setAutoCollapseListIds(prev => new Set(prev).add(approvedListId));
                        }}
                        highlightedListId={highlightedListId}
                        familyTimezone={selectedFamily?.timezone}
                        isHistoricalView={isChildViewingHistorical}
                        childBorderColor={getChildBorderColor(index)}
                        calendarExternalDate={calState?.selectedCalendarDate}
                        viewDateLabel={historicalDateLabel}
                      />
                    </>
                  );
                })()}

              </Paper>
              </DashboardTabPanel>
              );
            })}

            {/* Celebration Section — scoped to the active child tab (issue #1767) */}
            {activeTab > 0 && totalCelebrateCount > 0 && (
              isNaluV1Theme ? (
                <Box
                  id="celebration-section"
                  data-testid="celebration-section"
                  role="region"
                  aria-labelledby={`dashboard-tab-${activeTab}`}
                  sx={{ mt: '28px' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', mb: '14px' }}>
                    <Typography variant="h3" sx={{ fontSize: '24px' }}>Ready to celebrate</Typography>
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                      achieved goals waiting to be claimed
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: '16px' }}>
                    {goalsReadyToCelebrate.map((goal) => {
                      const accent = getGoalCelebrationAccent(goal.type);
                      const doneDateLabel = goal.completedAt
                        ? new Date(goal.completedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            ...(selectedFamily?.timezone ? { timeZone: selectedFamily.timezone } : {}),
                          })
                        : undefined;
                      return (
                        <Box
                          key={goal.id}
                          data-testid="goal-card"
                          sx={{
                            bgcolor: 'background.paper',
                            border: `1.5px solid ${accent.border}`,
                            borderRadius: '18px',
                            p: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '16px',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                            {getGoalTypeIcon(goal.type, 28)}
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 800, fontSize: '15px' }}>
                                {goal.title}{doneDateLabel ? ` — done ${doneDateLabel}` : ''}
                              </Typography>
                              {goal.rewardDescription && (
                                <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', fontStyle: 'italic' }}>
                                  {goal.rewardDescription}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Button
                            variant="contained"
                            onClick={() => handleCelebrateGoal(goal)}
                            sx={{
                              bgcolor: accent.button,
                              color: 'background.paper',
                              fontWeight: 800,
                              fontSize: '13px',
                              px: '18px',
                              py: '10px',
                              borderRadius: '999px',
                              flexShrink: 0,
                              boxShadow: 'none',
                              '&:hover': { bgcolor: accent.button, boxShadow: 'none', opacity: 0.9 },
                            }}
                          >
                            Celebrate ✦
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
              <Paper
                id="celebration-section"
                data-testid="celebration-section"
                role="region"
                aria-labelledby={`dashboard-tab-${activeTab}`}
                elevation={3}
                sx={{
                  p: 3,
                  mt: 4,
                  background: decorativeGradients.heroPurple,
                  color: 'white',
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'url(data:image/svg+xml,"%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                    opacity: 0.3,
                    pointerEvents: 'none'
                  }
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CelebrationIcon sx={{ fontSize: 28, mr: 1, color: celebrationGold }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, flexGrow: 1 }}>
                      🎉 Celebrate These Goals
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {goalsReadyToCelebrate.map((goal) => (
                      <Box
                        key={goal.id}
                        data-testid="goal-card"
                        sx={{
                          backgroundColor: alpha(theme.palette.common.white, 0.1),
                          borderRadius: 2,
                          p: 2,
                          backdropFilter: 'blur(10px)',
                          border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1, mr: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              {getGoalTypeIcon(goal.type)}
                              <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                                {goal.title}
                              </Typography>
                              {goal.isRecurring && (
                                <Tooltip title="Recurring goal">
                                  <Chip
                                    data-testid="repeat-icon"
                                    icon={<RepeatIcon sx={{ fontSize: 14 }} />}
                                    label="Recurring"
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      backgroundColor: alpha(theme.palette.common.white, 0.9),
                                      color: theme.palette.info.main,
                                      height: 20,
                                      '& .MuiChip-icon': {
                                        fontSize: 14,
                                        color: theme.palette.info.main
                                      }
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={getChildName(goal.childId)}
                                size="small"
                                sx={{
                                  backgroundColor: alpha(theme.palette.common.white, 0.2),
                                  color: 'white',
                                  fontWeight: 600
                                }}
                              />
                              {goal.completedAt && (
                                <Chip
                                  label={`Completed ${new Date(goal.completedAt).toLocaleDateString('en-US', selectedFamily?.timezone ? { timeZone: selectedFamily.timezone } : {})}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: alpha(theme.palette.success.main, 0.3),
                                    color: 'white',
                                    fontWeight: 600
                                  }}
                                />
                              )}
                            </Box>
                            {goal.rewardDescription && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                {goal.rewardImageUrl && (
                                  <Avatar
                                    src={getFullApiUrl(goal.rewardImageUrl)}
                                    alt="Reward image"
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      mr: 1,
                                      border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`
                                    }}
                                  />
                                )}
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                  🎁 {goal.rewardDescription}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          <Button
                            variant="contained"
                            onClick={() => handleCelebrateGoal(goal)}
                            sx={{
                              backgroundColor: celebrationGold,
                              color: theme.palette.text.primary,
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: celebrationGoldHover,
                                transform: 'translateY(-2px)',
                                boxShadow: `0 6px 16px ${alpha(celebrationGold, 0.4)}`
                              },
                              transition: 'all 0.2s ease'
                            }}
                            startIcon={<CelebrationIcon />}
                          >
                            Celebrate!
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  {totalCelebrateCount > celebrateRowsPerPage && (
                    <TablePagination
                      component="div"
                      count={totalCelebrateCount}
                      page={celebratePage}
                      onPageChange={(_event, newPage) => setCelebratePage(newPage)}
                      rowsPerPage={celebrateRowsPerPage}
                      onRowsPerPageChange={(event) => {
                        setCelebrateRowsPerPage(parseInt(event.target.value, 10));
                        setCelebratePage(0);
                      }}
                      rowsPerPageOptions={[5, 10, 25]}
                      data-testid="celebrate-pagination"
                      aria-label="Celebrate goals pagination"
                      sx={{
                        color: 'white',
                        '& .MuiTablePagination-selectIcon': { color: 'white' },
                        '& .MuiTablePagination-actions button': { color: 'white' },
                      }}
                    />
                  )}
                </Box>
              </Paper>
              )
            )}
        </>
      </Container>

      {/* Floating Microphone */}
      <Fab
        color="secondary"
        size="large"
        data-testid="mic-button"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
          transform: isRecording ? 'scale(1.1)' : 'scale(1)',
          transition: 'transform 0.2s',
          bgcolor: isRecording ? 'error.main' : 'secondary.main',
          display: 'none', // Hidden until voice recording feature is ready
        }}
        onMouseDown={handleMicrophonePress}
        onMouseUp={handleMicrophoneRelease}
        onTouchStart={handleMicrophonePress}
        onTouchEnd={handleMicrophoneRelease}
      >
        <MicIcon />
      </Fab>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Celebration Modal */}
      <CelebrationModal
        isOpen={celebrationModal.isOpen}
        goal={celebrationModal.goal}
        childName={celebrationModal.childName}
        onClose={() => setCelebrationModal({ isOpen: false, goal: null, childName: '' })}
        onCelebrated={handleGoalCelebrated}
      />

      {/* Nuclear Reset Confirmation Dialog (Issue #1310) */}
      <Dialog
        open={nukeState.childId !== null}
        onClose={() => !nukeState.loading && setNukeState({ childId: null, loading: false, error: null })}
        aria-labelledby="nuke-dialog-title"
      >
        <DialogTitle id="nuke-dialog-title" sx={{ color: 'error.main' }}>
          ☢️ Nuclear Reset
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>This cannot be undone.</strong>
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            All active, non-exempt goals will be marked as failed and recreated from zero.
            Completed goals and goals marked &quot;Exempt from nuclear reset&quot; will not be affected.
          </DialogContentText>
          {nukeState.error && (
            <Alert severity="error" sx={{ mt: 2 }}>{nukeState.error}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setNukeState({ childId: null, loading: false, error: null })}
            disabled={nukeState.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleNukeConfirm}
            color="error"
            variant="contained"
            disabled={nukeState.loading}
            startIcon={nukeState.loading ? <CircularProgress size={16} /> : <NukeIcon />}
          >
            {nukeState.loading ? 'Resetting...' : 'Reset All Goals'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Away Day Recording Warning Dialog (issue #1706) */}
      <Dialog
        open={awayDayConfirmPending !== null}
        onClose={() => setAwayDayConfirmPending(null)}
        aria-labelledby="away-day-dialog-title"
      >
        <DialogTitle id="away-day-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlightTakeoffIcon color="info" />
          Today is an Away Day
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This child is marked as away today. Chores and heart requirements are already waived.
            Do you still want to record this behavior?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAwayDayConfirmPending(null)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (awayDayConfirmPending) {
                handleBehaviorClick(awayDayConfirmPending.childId, awayDayConfirmPending.behaviorType);
              }
            }}
          >
            Record This One
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setAwayDayDismissedForToday(true);
              if (awayDayConfirmPending) {
                handleBehaviorClick(awayDayConfirmPending.childId, awayDayConfirmPending.behaviorType);
              }
            }}
          >
            Record All Today
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardRedesigned;